// src/store/modules/summary.js 完整示例

const state = {
  // ========== 核心数据 ==========
  // 当前会议总结
  currentSummary: null,
  
  // 总结生成状态
  summaryStatus: {
    isGenerating: false,      // 是否正在生成
    canGenerate: false,       // 是否可以生成（转写结束后）
    progress: 0,             // 生成进度
    lastGenerated: null      // 上次生成时间
  },
  
  // 总结历史记录
  historySummaries: [],
  
  // 总结模板设置
  summarySettings: {
    includeTopics: true,     // 包含关键议题
    includeActions: true,    // 包含行动项
    includeMinutes: true,    // 包含会议纪要
    autoGenerate: true       // 转写结束后自动生成
  }
};

const mutations = {
  SET_CURRENT_SUMMARY(state, summary) {
    state.currentSummary = summary;
  },
  
  SET_GENERATING_STATUS(state, status) {
    state.summaryStatus.isGenerating = status;
  },
  
  SET_CAN_GENERATE(state, canGenerate) {
    state.summaryStatus.canGenerate = canGenerate;
  },
  
  SET_GENERATION_PROGRESS(state, progress) {
    state.summaryStatus.progress = progress;
  },
  
  ADD_TO_HISTORY(state, summary) {
    state.historySummaries.unshift(summary);
  }
};

const actions = {
  // ========== 核心方法 ==========
  
  /**
   * 生成会议总结（主要方法）
   * 流程：转录文本 → 智能分析 → 生成总结
   */
  async generateSummary({ commit, dispatch, rootState }, meetingId) {
    commit('SET_GENERATING_STATUS', true);
    commit('SET_GENERATION_PROGRESS', 10);
    
    try {
      // 1. 获取转录文本
      const transcriptionText = rootState.transcription.transcriptionText;
      commit('SET_GENERATION_PROGRESS', 30);
      
      // 2. 调用后端API生成总结
      const response = await this._vm.$api.summary.generate({
        meetingId,
        transcriptionText,
        settings: rootState.summary.summarySettings
      });
      commit('SET_GENERATION_PROGRESS', 70);
      
      // 3. 处理总结数据
      const summary = {
        id: response.summaryId,
        meetingId: meetingId,
        meetingTitle: rootState.meeting.currentMeeting?.title || '会议总结',
        generateTime: new Date(),
        content: response.content,
        rawData: response.rawData,
        statistics: {
          totalDuration: rootState.transcription.duration,
          wordCount: rootState.transcription.wordCount,
          speakerCount: Object.keys(rootState.transcription.speakerStats).length
        }
      };
      
      commit('SET_CURRENT_SUMMARY', summary);
      commit('ADD_TO_HISTORY', summary);
      commit('SET_CAN_GENERATE', false);
      commit('SET_GENERATION_PROGRESS', 100);
      
      // 4. 保存到本地/后端
      await dispatch('saveSummaryToStorage', summary);
      
      return summary;
    } catch (error) {
      console.error('生成总结失败:', error);
      throw error;
    } finally {
      setTimeout(() => {
        commit('SET_GENERATING_STATUS', false);
        commit('SET_GENERATION_PROGRESS', 0);
      }, 1000);
    }
  },
  
  /**
   * 从转录模块接收通知，可以生成总结
   */
  onTranscriptionCompleted({ commit, dispatch }, transcriptionData) {
    commit('SET_CAN_GENERATE', true);
    
    // 如果设置了自动生成，则立即生成
    if (rootState.summary.summarySettings.autoGenerate) {
      dispatch('generateSummary');
    }
  },
  
  /**
   * 获取历史总结
   */
  async fetchHistorySummaries({ commit }) {
    try {
      const response = await this._vm.$api.summary.getHistory();
      commit('SET_HISTORY_SUMMARIES', response);
      return response;
    } catch (error) {
      console.error('获取历史总结失败:', error);
      throw error;
    }
  },
  
  /**
   * 导出总结（多种格式）
   */
  async exportSummary({ state }, { format = 'markdown', summaryId = null }) {
    const summary = summaryId 
      ? state.historySummaries.find(s => s.id === summaryId)
      : state.currentSummary;
    
    if (!summary) {
      throw new Error('没有可导出的总结');
    }
    
    try {
      const response = await this._vm.$api.summary.export({
        summaryId: summary.id,
        format: format // markdown, pdf, docx, html
      });
      
      return response;
    } catch (error) {
      console.error('导出总结失败:', error);
      throw error;
    }
  },
  
  /**
   * 分享总结
   */
  async shareSummary({ state }, { summaryId, shareTo = [] }) {
    const summary = state.currentSummary || 
                    state.historySummaries.find(s => s.id === summaryId);
    
    try {
      const response = await this._vm.$api.summary.share({
        summaryId: summary.id,
        recipients: shareTo,
        message: `会议总结：${summary.meetingTitle}`
      });
      
      return response;
    } catch (error) {
      console.error('分享总结失败:', error);
      throw error;
    }
  },
  
  /**
   * 保存总结到本地存储
   */
  saveSummaryToStorage({ state }, summary) {
    try {
      const summaries = JSON.parse(localStorage.getItem('meetingSummaries') || '[]');
      summaries.unshift(summary);
      localStorage.setItem('meetingSummaries', JSON.stringify(summaries.slice(0, 100))); // 最多保存100条
    } catch (error) {
      console.error('保存总结到本地存储失败:', error);
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};